import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ShieldAlert,
  Activity,
  DollarSign,
  Award,
  Search,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  ChevronRight,
  Info,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  UserCheck,
  Stethoscope,
  X,
  SlidersHorizontal,
  ChevronLeft,
  Scale
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend
} from 'recharts';
import apiService from '../services/apiService';
import { formatCurrency } from '../utils/formatters';
import PhysicianBenchmarkingView from './PhysicianBenchmarkingView';

export type PhysicianReportType =
  | 'physician-fraud'
  | 'physician-inpatient-outpatient'
  | 'physician-change-amount'
  | 'physician-performance'
  | 'physician-benchmarking';

interface PhysicianReportsViewProps {
  activeReport: PhysicianReportType;
  setActiveReport: (report: PhysicianReportType) => void;
}

export const PhysicianReportsView: React.FC<PhysicianReportsViewProps> = ({
  activeReport,
  setActiveReport,
}) => {
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Physician selector state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedPhysicianName, setSelectedPhysicianName] = useState<string>('all');
  const [riskFilter, setRiskFilter] = useState<'all' | 'high' | 'review' | 'clean'>('all');

  // Live database claims state
  const [liveDbClaims, setLiveDbClaims] = useState<any[]>([]);
  const [loadingDbClaims, setLoadingDbClaims] = useState<boolean>(false);
  const [claimsViewMode, setClaimsViewMode] = useState<'flagged' | 'all_db'>('flagged');
  const [expandedClaimId, setExpandedClaimId] = useState<string | null>(null);

  // Invoices table pagination & search
  const [invoiceSearch, setInvoiceSearch] = useState<string>('');
  const [invoicesPage, setInvoicesPage] = useState<number>(1);
  const invoicesPerPage = 10;

  // Directory table pagination & sorting
  const [directoryPage, setDirectoryPage] = useState<number>(1);
  const directoryPerPage = 10;
  const [sortField, setSortField] = useState<'name' | 'claims' | 'clean' | 'fraud' | 'billed'>('claims');
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  // Load latest produced report from backend
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getLatestFraudReport();
      setReportData(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load physician reports telemetry');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // When selected physician changes, load their live DB claims if needed
  useEffect(() => {
    setInvoicesPage(1);
    setExpandedClaimId(null);

    if (selectedPhysicianName !== 'all') {
      setLoadingDbClaims(true);
      apiService.getPhysicianClaims(selectedPhysicianName, 1, 30)
        .then((res: any) => {
          const claims = res?.results || res?.claims || [];
          setLiveDbClaims(claims);
        })
        .catch((err) => {
          console.warn('Could not fetch live DB claims for doctor:', err);
          setLiveDbClaims([]);
        })
        .finally(() => {
          setLoadingDbClaims(false);
        });
    } else {
      setLiveDbClaims([]);
      setClaimsViewMode('flagged');
    }
  }, [selectedPhysicianName]);

  const summary = useMemo(() => reportData?.summary || {}, [reportData]);
  const pillarsSummary = useMemo(() => summary?.pillars_summary || {}, [summary]);
  const physiciansMap = useMemo(() => reportData?.physicians || {}, [reportData]);

  // List of all physicians
  const physiciansList: any[] = useMemo(() => {
    return Object.values(physiciansMap);
  }, [physiciansMap]);

  // Dynamic Risk Counter Pills
  const riskCounts = useMemo(() => {
    let all = physiciansList.length;
    let high = 0;
    let review = 0;
    let clean = 0;
    physiciansList.forEach((p: any) => {
      const rate = p.fraud_rate_pct ?? (p.fraud_detection?.fraud_rate_pct || 0);
      if (rate >= 10.0) high++;
      else if (rate >= 5.0) review++;
      else clean++;
    });
    return { all, high, review, clean };
  }, [physiciansList]);

  // Filtered and sorted physicians list for directory table
  const filteredPhysicians = useMemo(() => {
    const list = physiciansList.filter((p: any) => {
      const nameMatch = (p.physician_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.specialty || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      if (!nameMatch) return false;

      const rate = p.fraud_rate_pct ?? (p.fraud_detection?.fraud_rate_pct || 0);
      if (riskFilter === 'high') return rate >= 10.0;
      if (riskFilter === 'review') return rate >= 5.0 && rate < 10.0;
      if (riskFilter === 'clean') return rate < 5.0;
      return true;
    });

    list.sort((a: any, b: any) => {
      let valA: any = 0;
      let valB: any = 0;

      if (sortField === 'name') {
        valA = a.physician_name || '';
        valB = b.physician_name || '';
        return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      if (sortField === 'claims') {
        valA = a.total_claims || 0;
        valB = b.total_claims || 0;
      } else if (sortField === 'clean') {
        valA = 100 - (a.fraud_rate_pct ?? 0);
        valB = 100 - (b.fraud_rate_pct ?? 0);
      } else if (sortField === 'fraud') {
        valA = a.fraud_rate_pct ?? 0;
        valB = b.fraud_rate_pct ?? 0;
      } else if (sortField === 'billed') {
        valA = a.total_billed_amount || 0;
        valB = b.total_billed_amount || 0;
      }
      return sortAsc ? valA - valB : valB - valA;
    });

    return list;
  }, [physiciansList, searchQuery, riskFilter, sortField, sortAsc]);

  // Dynamic Cohort Metrics calculated based on active filters (Search / Risk Level)
  const filteredMetrics = useMemo(() => {
    const activeList = filteredPhysicians;
    const count = activeList.length;
    let totalClaims = 0;
    let fraudClaims = 0;
    let billedAmount = 0;
    let fraudExposure = 0;

    activeList.forEach((p: any) => {
      const claims = Number(p.total_claims || 0);
      totalClaims += claims;
      fraudClaims += Number(p.fraud_claims_count || 0);
      const billed = Number(p.total_billed_amount || 0);
      billedAmount += billed;
      const rate = Number(p.fraud_rate_pct || 0);
      const exp = p.fraud_detection?.total_fraud_amount != null
        ? Number(p.fraud_detection.total_fraud_amount)
        : (billed * (rate / 100));
      fraudExposure += exp;
    });

    const fraudRate = totalClaims > 0 ? (fraudClaims / totalClaims) * 100 : 0;
    const cleanRate = 100 - fraudRate;

    return {
      count,
      totalClaims,
      fraudClaims,
      billedAmount,
      fraudExposure,
      fraudRate,
      cleanRate
    };
  }, [filteredPhysicians]);

  // Selected physician details (if not 'all')
  const selectedPhysician = useMemo(() => {
    if (selectedPhysicianName === 'all') return null;
    return physiciansMap[selectedPhysicianName] || null;
  }, [selectedPhysicianName, physiciansMap]);

  // Top 8 physicians sorted by fraud claims count for network charts (dynamically reflects active cohort)
  const topFraudPhysicians = useMemo(() => {
    const sourceList = (filteredPhysicians.length > 0 && (riskFilter !== 'all' || searchQuery.trim()))
      ? filteredPhysicians
      : physiciansList;
    return [...sourceList]
      .sort((a, b) => (b.fraud_claims_count || 0) - (a.fraud_claims_count || 0))
      .slice(0, 8);
  }, [filteredPhysicians, physiciansList, riskFilter, searchQuery]);

  // All claims pool before table search (dynamically filtered by doctor or risk cohort)
  const rawClaimsPool = useMemo(() => {
    if (claimsViewMode === 'all_db' && liveDbClaims.length > 0) {
      return liveDbClaims;
    }
    if (selectedPhysician) {
      return selectedPhysician.fraud_cases_sample || [];
    }
    const allCases = reportData?.top_fraud_cases || [];
    if (riskFilter === 'all' && !searchQuery.trim()) {
      return allCases;
    }
    const matchingDoctorNames = new Set(filteredPhysicians.map((p: any) => p.physician_name));
    const cohortCases = allCases.filter((c: any) => matchingDoctorNames.has(c.treating_physician));
    return cohortCases.length > 0 ? cohortCases : allCases;
  }, [claimsViewMode, liveDbClaims, selectedPhysician, reportData, riskFilter, searchQuery, filteredPhysicians]);

  // Invoices filtered by search
  const filteredClaims = useMemo(() => {
    if (!invoiceSearch.trim()) return rawClaimsPool;
    const q = invoiceSearch.toLowerCase();
    return rawClaimsPool.filter((c: any) =>
      (c.invoice_no || '').toLowerCase().includes(q) ||
      (c.patient_name || '').toLowerCase().includes(q) ||
      (c.member_id || '').toLowerCase().includes(q) ||
      (c.treating_physician || '').toLowerCase().includes(q) ||
      (c.primary_diag_code || '').toLowerCase().includes(q)
    );
  }, [rawClaimsPool, invoiceSearch]);

  // Invoices paginated slice
  const paginatedClaims = useMemo(() => {
    const start = (invoicesPage - 1) * invoicesPerPage;
    return filteredClaims.slice(start, start + invoicesPerPage);
  }, [filteredClaims, invoicesPage]);

  const totalInvoicesPages = Math.ceil(filteredClaims.length / invoicesPerPage) || 1;

  // Directory paginated slice
  const paginatedDirectory = useMemo(() => {
    const start = (directoryPage - 1) * directoryPerPage;
    return filteredPhysicians.slice(start, start + directoryPerPage);
  }, [filteredPhysicians, directoryPage]);

  const totalDirectoryPages = Math.ceil(filteredPhysicians.length / directoryPerPage) || 1;

  // Service Category Breakdown data for Pillar 2
  const serviceCategoryChartData = useMemo(() => {
    return [
      { category: 'Pharmacy (P)', claims: 14810, amount: 2154320 },
      { category: 'Consultation (C)', claims: 8245, amount: 1421090 },
      { category: 'Diagnostics/Lab (L)', claims: 4120, amount: 785400 },
      { category: 'Dental/Other (D)', claims: 2622, amount: 286238 },
    ];
  }, []);

  // Export claims to CSV
  const handleExportClaimsCSV = () => {
    if (!filteredClaims || filteredClaims.length === 0) return;
    const headers = ['Invoice No', 'Treating Physician', 'Member ID', 'Patient Name', 'Service Date', 'Gross Amount', 'Fraud Score', 'ICD-10', 'Action'];
    const rows = filteredClaims.map((c: any) => [
      c.invoice_no || '',
      c.treating_physician || selectedPhysicianName,
      c.member_id || '',
      c.patient_name || '',
      c.service_date ? String(c.service_date).substring(0, 10) : '',
      c.gross_claim_amount || 0,
      c.fraud_score || 0,
      c.primary_diag_code || '',
      (c.fraud_score || 0) >= 0.15 ? 'Immediate Audit' : 'Pass / Normal',
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((row: any[]) => row.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `claims_audit_${selectedPhysicianName.replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSort = (field: 'name' | 'claims' | 'clean' | 'fraud' | 'billed') => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[480px] p-12 glass-card rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xl">
        <div className="relative mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-primary-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-primary-500/25">
            <RefreshCw className="w-7 h-7 text-white animate-spin" />
          </div>
        </div>
        <h3 className="text-base font-extrabold text-slate-800 dark:text-slate-100">Synchronizing Telemetry</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Aggregating multi-layer fraud scores and clinical pillars across all records...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-2xl text-center space-y-4 max-w-lg mx-auto shadow-xl">
        <div className="w-12 h-12 rounded-2xl bg-rose-100 dark:bg-rose-900/60 text-rose-600 flex items-center justify-center mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-900 dark:text-white">Telemetry Synchronization Error</h3>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{error}</p>
        </div>
        <button
          onClick={loadData}
          className="px-5 py-2.5 bg-primary-600 hover:bg-primary-500 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-primary-600/30 cursor-pointer"
        >
          Retry Connection
        </button>
      </div>
    );
  }

  // Doctor initials
  const doctorInitials = selectedPhysician?.physician_name
    ? selectedPhysician.physician_name
        .split(' ')
        .map((n: string) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'DR';

  return (
    <div className="space-y-6 pb-28 animate-fade-in">
      {/* ── Top Header & Tab Navigation Bar ── */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-slate-200/80 dark:border-slate-800">
        <div>
          <div className="flex items-center flex-wrap gap-2 mb-1.5">
            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
              <span>Reports</span>
              <span>/</span>
              <span className="text-primary-600 dark:text-primary-400 font-bold">Physician Reports</span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-full border border-emerald-200/60 dark:border-emerald-800/60">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Verified Telemetry Feed
            </span>
            {selectedPhysician && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 px-2.5 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800">
                <UserCheck className="w-3 h-3" />
                Focus: Dr. {selectedPhysician.physician_name}
                <button
                  onClick={() => setSelectedPhysicianName('')}
                  className="hover:text-indigo-950 dark:hover:text-white ml-0.5 cursor-pointer"
                  title="Clear doctor focus"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white">
            {activeReport === 'physician-fraud' && (selectedPhysician ? `Fraud Detection: Dr. ${selectedPhysician.physician_name}` : 'Physician Fraud Detection Report')}
            {activeReport === 'physician-inpatient-outpatient' && (selectedPhysician ? `In/Out-Patient Analysis: Dr. ${selectedPhysician.physician_name}` : 'In-Patient / Out-Patient Analysis')}
            {activeReport === 'physician-change-amount' && (selectedPhysician ? `Financial Reconciliation: Dr. ${selectedPhysician.physician_name}` : 'Change of Amount Financial Report')}
            {activeReport === 'physician-performance' && (selectedPhysician ? `Peer Performance: Dr. ${selectedPhysician.physician_name}` : 'Physician Performance & Peer Benchmarks')}
            {activeReport === 'physician-benchmarking' && (selectedPhysician ? `Peer Benchmarking: Dr. ${selectedPhysician.physician_name}` : 'Physician Specialty Peer Benchmarking')}
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-2xl leading-relaxed">
            {activeReport === 'physician-fraud' && 'Multi-layer statistical anomaly screening across 74 treating physicians and 29,797 claim records.'}
            {activeReport === 'physician-inpatient-outpatient' && 'Ambulatory vs hospitalization admission ratios and enterprise specialist benchmarks.'}
            {activeReport === 'physician-change-amount' && 'Financial variance tracking, gross vs net payable reconciliation, deductible & copay deductions.'}
            {activeReport === 'physician-performance' && 'Clinical quality benchmarks, clean claims approval metrics, and automated audit queues.'}
            {activeReport === 'physician-benchmarking' && 'Clinically fair peer-group comparisons strictly isolated by Specialty & Sub-specialty. Never compared against all physicians globally.'}
          </p>
        </div>

        {/* Quick Report Switcher Segmented Control (Non-wrapping single-line) */}
        <div className="inline-flex items-center p-1 bg-slate-100/90 dark:bg-slate-800/90 rounded-xl border border-slate-200/80 dark:border-slate-700/80 shrink-0 self-start lg:self-center overflow-x-auto max-w-full">
          <button
            onClick={() => setActiveReport('physician-benchmarking')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeReport === 'physician-benchmarking'
                ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm border border-slate-200/60 dark:border-slate-600'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Scale className="w-3.5 h-3.5 text-indigo-500" />
            <span>Benchmarking</span>
          </button>
          <button
            onClick={() => setActiveReport('physician-fraud')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeReport === 'physician-fraud'
                ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm border border-slate-200/60 dark:border-slate-600'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <ShieldAlert className="w-3.5 h-3.5 text-rose-500" />
            <span>Fraud</span>
          </button>
          <button
            onClick={() => setActiveReport('physician-inpatient-outpatient')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeReport === 'physician-inpatient-outpatient'
                ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm border border-slate-200/60 dark:border-slate-600'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Activity className="w-3.5 h-3.5 text-blue-500" />
            <span>In/Out-Patient</span>
          </button>
          <button
            onClick={() => setActiveReport('physician-change-amount')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeReport === 'physician-change-amount'
                ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm border border-slate-200/60 dark:border-slate-600'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
            <span>Amount</span>
          </button>
          <button
            onClick={() => setActiveReport('physician-performance')}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
              activeReport === 'physician-performance'
                ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm border border-slate-200/60 dark:border-slate-600'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Award className="w-3.5 h-3.5 text-amber-500" />
            <span>Performance</span>
          </button>
        </div>
      </div>

      {activeReport === 'physician-benchmarking' && (
        <PhysicianBenchmarkingView
          initialPhysicianName={selectedPhysicianName !== 'all' ? selectedPhysicianName : undefined}
          onSelectPhysician={(doc) => setSelectedPhysicianName(doc)}
        />
      )}

      {activeReport !== 'physician-benchmarking' && (
        <>
          {/* ── Refined Filter & Physician Selector Bar ── */}
          <div className="glass-card rounded-2xl p-4 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 shadow-sm border border-slate-200/80 dark:border-slate-800">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search physician name or specialty..."
              className="w-full pl-10 pr-9 py-2.5 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all font-medium"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Risk Level Filter Pills */}
          <div className="flex items-center gap-1 overflow-x-auto py-1">
            <button
              onClick={() => {
                setRiskFilter('all');
                setSelectedPhysicianName('all');
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                riskFilter === 'all'
                  ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              All ({riskCounts.all})
            </button>
            <button
              onClick={() => {
                setRiskFilter('high');
                setSelectedPhysicianName('all');
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                riskFilter === 'high'
                  ? 'bg-rose-600 text-white shadow-sm ring-2 ring-rose-500/30'
                  : 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/60'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
              High Risk ({riskCounts.high})
            </button>
            <button
              onClick={() => {
                setRiskFilter('review');
                setSelectedPhysicianName('all');
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                riskFilter === 'review'
                  ? 'bg-amber-600 text-white shadow-sm ring-2 ring-amber-500/30'
                  : 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/60'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Review ({riskCounts.review})
            </button>
            <button
              onClick={() => {
                setRiskFilter('clean');
                setSelectedPhysicianName('all');
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                riskFilter === 'clean'
                  ? 'bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-500/30'
                  : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/60'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Clean ({riskCounts.clean})
            </button>
          </div>
        </div>

        {/* Selected Physician Focus Dropdown */}
        <div className="flex items-center gap-2 justify-end pt-2 lg:pt-0 border-t lg:border-t-0 border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500 whitespace-nowrap">Focus:</span>
            <select
              value={selectedPhysicianName}
              onChange={(e) => setSelectedPhysicianName(e.target.value)}
              className="px-3.5 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer min-w-[210px] shadow-sm"
            >
              <option value="all">Network Aggregate ({filteredPhysicians.length} Physicians)</option>
              {filteredPhysicians.map((p: any) => (
                <option key={p.physician_name} value={p.physician_name}>
                  Dr. {p.physician_name} ({p.fraud_rate_pct ?? p.fraud_detection?.fraud_rate_pct}%)
                </option>
              ))}
            </select>
          </div>

          {selectedPhysicianName !== 'all' && (
            <button
              onClick={() => setSelectedPhysicianName('all')}
              className="px-2.5 py-2 text-xs text-primary-600 dark:text-primary-400 font-bold hover:bg-primary-50 dark:hover:bg-primary-950/60 rounded-xl transition-colors cursor-pointer"
              title="Reset to Network View"
            >
              Reset
            </button>
          )}

          <button
            onClick={loadData}
            title="Refresh Live Telemetry"
            className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer shadow-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── Active Risk / Search Cohort Banner & Quick Doctor Selector Strip ── */}
      {!selectedPhysician && (riskFilter !== 'all' || searchQuery.trim()) && (
        <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 dark:from-slate-850 dark:via-slate-800 dark:to-slate-850 text-white shadow-lg border border-slate-800 dark:border-slate-700 space-y-3.5 animate-fade-in">
          <div className="flex items-center justify-between flex-wrap gap-2.5">
            <div className="flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full animate-pulse ${
                riskFilter === 'high' ? 'bg-rose-500 ring-4 ring-rose-500/20' :
                riskFilter === 'review' ? 'bg-amber-500 ring-4 ring-amber-500/20' :
                riskFilter === 'clean' ? 'bg-emerald-500 ring-4 ring-emerald-500/20' : 'bg-primary-400 ring-4 ring-primary-400/20'
              }`} />
              <div>
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-sm font-black tracking-tight text-white">
                    {riskFilter === 'high' && `Active Cohort: ${filteredPhysicians.length} High-Risk Physicians`}
                    {riskFilter === 'review' && `Active Cohort: ${filteredPhysicians.length} Review-Flagged Physicians`}
                    {riskFilter === 'clean' && `Active Cohort: ${filteredPhysicians.length} Benchmark Clean Physicians`}
                    {riskFilter === 'all' && searchQuery && `Found ${filteredPhysicians.length} Physicians matching "${searchQuery}"`}
                  </span>
                  <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                    riskFilter === 'high' ? 'bg-rose-500/30 text-rose-300 border border-rose-500/40' :
                    riskFilter === 'review' ? 'bg-amber-500/30 text-amber-300 border border-amber-500/40' :
                    riskFilter === 'clean' ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-500/40' : 'bg-primary-500/30 text-primary-300'
                  }`}>
                    {riskFilter === 'high' ? '≥10.0% Fraud Rate' : riskFilter === 'review' ? '5.0% - 10.0% Rate' : riskFilter === 'clean' ? '<5.0% Clean Rate' : 'Search Filter'}
                  </span>
                </div>
                <p className="text-xs text-slate-300 font-mono mt-0.5">
                  Aggregated: <span className="font-bold text-white">{filteredMetrics.totalClaims.toLocaleString()}</span> claims • <span className="font-bold text-rose-300">{filteredMetrics.fraudClaims.toLocaleString()}</span> anomalies ({filteredMetrics.fraudRate.toFixed(1)}% rate) • <span className="font-bold text-emerald-300">{formatCurrency(filteredMetrics.fraudExposure)}</span> exposure
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                setRiskFilter('all');
                setSearchQuery('');
                setSelectedPhysicianName('all');
              }}
              className="text-xs font-bold text-slate-300 hover:text-white px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
            >
              <X className="w-3.5 h-3.5" />
              <span>Reset to All (74)</span>
            </button>
          </div>

          {/* Quick Doctor Selector Strip */}
          <div className="space-y-1.5 pt-1 border-t border-slate-800/80">
            <span className="text-[10px] uppercase font-extrabold text-slate-400 tracking-wider block">
              Click any doctor in this cohort to focus their profile & claims:
            </span>
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
              {filteredPhysicians.slice(0, 15).map((p: any) => {
                const rate = Number(p.fraud_rate_pct ?? (p.fraud_detection?.fraud_rate_pct || 0));
                return (
                  <button
                    key={p.physician_name}
                    onClick={() => setSelectedPhysicianName(p.physician_name)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold shrink-0 transition-all cursor-pointer bg-slate-800/90 hover:bg-slate-700 text-slate-100 hover:text-white border border-slate-700/80 hover:border-slate-500 shadow-sm"
                  >
                    <span className="font-bold">Dr. {p.physician_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold ${
                      rate >= 10.0 ? 'bg-rose-500/30 text-rose-300' :
                      rate >= 5.0 ? 'bg-amber-500/30 text-amber-300' : 'bg-emerald-500/30 text-emerald-300'
                    }`}>
                      {rate.toFixed(1)}%
                    </span>
                  </button>
                );
              })}
              {filteredPhysicians.length > 15 && (
                <span className="text-[11px] text-slate-400 shrink-0 px-2 font-mono">
                  +{filteredPhysicians.length - 15} more in directory
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Doctor Focus Highlight Banner (When a Single Physician is Selected) ── */}
      {selectedPhysician && (
        <div className="p-5 bg-gradient-to-r from-blue-50/90 via-indigo-50/70 to-primary-50/90 dark:from-slate-900 dark:via-indigo-950/40 dark:to-slate-900 border border-indigo-200/80 dark:border-indigo-800/80 rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-sm animate-fade-in">
          <div className="flex items-center gap-4">
            <div className="w-13 h-13 rounded-2xl bg-gradient-to-br from-primary-600 to-indigo-600 text-white font-black text-base flex items-center justify-center shadow-lg shadow-primary-500/30 ring-4 ring-white dark:ring-slate-800 shrink-0">
              {doctorInitials}
            </div>
            <div>
              <div className="flex items-center flex-wrap gap-2">
                <h3 className="text-lg font-black text-slate-900 dark:text-white">
                  Dr. {selectedPhysician.physician_name}
                </h3>
                <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300">
                  Specialty {selectedPhysician.specialty || 'General'}
                </span>
                <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full flex items-center gap-1 ${
                  (selectedPhysician.fraud_rate_pct || 0) >= 10.0
                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300'
                    : (selectedPhysician.fraud_rate_pct || 0) >= 5.0
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    (selectedPhysician.fraud_rate_pct || 0) >= 10.0 ? 'bg-rose-500' : (selectedPhysician.fraud_rate_pct || 0) >= 5.0 ? 'bg-amber-500' : 'bg-emerald-500'
                  }`} />
                  {(selectedPhysician.fraud_rate_pct || 0) >= 10.0 ? 'High Risk' : (selectedPhysician.fraud_rate_pct || 0) >= 5.0 ? 'Review Required' : 'Clean Audit'}
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                Facility: <span className="font-bold text-slate-800 dark:text-slate-200">{selectedPhysician.provider_name || 'Sinan'}</span> • Total Encounters: <span className="font-extrabold font-mono text-slate-900 dark:text-white">{selectedPhysician.total_claims?.toLocaleString()}</span> • Total Billed: <span className="font-extrabold font-mono text-slate-900 dark:text-white">{formatCurrency(selectedPhysician.total_billed_amount)}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            <div className="p-2.5 bg-white/80 dark:bg-slate-800/80 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 text-right px-4">
              <span className="text-[10px] font-bold text-slate-400 block uppercase tracking-wider">Peer Clean Approval</span>
              <span className="text-lg font-black font-mono text-emerald-600 dark:text-emerald-400">
                {selectedPhysician.physician_performance?.clean_claims_approval_rate_pct || (100 - (selectedPhysician.fraud_rate_pct || 0)).toFixed(1)}%
              </span>
            </div>
            <button
              onClick={() => setSelectedPhysicianName('all')}
              className="px-3.5 py-2.5 rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
              <span>Back to Network</span>
            </button>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          REPORT VIEW 1: FRAUD DETECTION
         ════════════════════════════════════════════════════════════════════════ */}
      {activeReport === 'physician-fraud' && (
        <div className="space-y-6">
          {/* Bento KPI Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-blue-600 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {selectedPhysician ? 'Claims Evaluated' : (riskFilter !== 'all' || searchQuery.trim()) ? `${riskFilter === 'all' ? 'Filtered' : riskFilter.toUpperCase()} Claims (${filteredMetrics.count} Drs)` : 'Claims Evaluated'}
                </span>
                <span className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400">
                  <Stethoscope className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white font-mono">
                {(selectedPhysician ? selectedPhysician.total_claims : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.totalClaims : pillarsSummary.fraud_detection?.total_claims_evaluated || summary.total_claims || 0).toLocaleString()}
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                {selectedPhysician ? `Dr. ${selectedPhysician.physician_name} clinical encounters` : (riskFilter !== 'all' || searchQuery.trim()) ? `Screened across ${filteredMetrics.count} active physicians` : 'Facility-wide records screened'}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-rose-600 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {selectedPhysician ? 'Flagged Anomalies' : (riskFilter !== 'all' || searchQuery.trim()) ? `Cohort Anomalies (${filteredMetrics.count} Drs)` : 'Flagged Anomalies'}
                </span>
                <span className="p-2 rounded-xl bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400">
                  <ShieldAlert className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-rose-600 font-mono">
                {(selectedPhysician ? selectedPhysician.fraud_claims_count : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.fraudClaims : pillarsSummary.fraud_detection?.total_fraud_cases || summary.total_fraud_claims || 0).toLocaleString()}
              </p>
              <p className="text-xs text-rose-500 font-semibold mt-1">
                {selectedPhysician ? `${selectedPhysician.fraud_claims_count} service lines flagged` : (riskFilter !== 'all' || searchQuery.trim()) ? `${filteredMetrics.fraudRate.toFixed(1)}% anomaly rate in cohort` : 'Audit flagged cases across facility'}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-amber-600 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {selectedPhysician ? 'Physician Fraud Rate' : (riskFilter !== 'all' || searchQuery.trim()) ? 'Cohort Fraud Rate' : 'Overall Fraud Rate'}
                </span>
                <span className="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400">
                  <Activity className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-amber-600 font-mono">
                {Number(selectedPhysician ? selectedPhysician.fraud_rate_pct : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.fraudRate : pillarsSummary.fraud_detection?.overall_fraud_rate_pct || summary.fraud_rate_pct || 0).toFixed(2)}%
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                {(riskFilter !== 'all' || searchQuery.trim()) ? (riskFilter === 'high' ? 'High risk threshold (≥10%)' : riskFilter === 'review' ? 'Under clinical review (5%-10%)' : riskFilter === 'clean' ? 'Exemplary benchmark (<5%)' : 'Filtered cohort average') : 'Multi-layer statistical confidence'}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-emerald-600 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {selectedPhysician ? 'Flagged Exposure' : (riskFilter !== 'all' || searchQuery.trim()) ? 'Cohort Exposure' : 'Flagged Exposure'}
                </span>
                <span className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400">
                  <DollarSign className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-emerald-700 dark:text-emerald-400 font-mono">
                {formatCurrency(selectedPhysician ? (selectedPhysician.fraud_detection?.total_fraud_amount || selectedPhysician.total_billed_amount * (selectedPhysician.fraud_rate_pct / 100)) : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.fraudExposure : pillarsSummary.fraud_detection?.total_fraud_amount_flagged || summary.total_fraud_amount || 0)}
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                {(riskFilter !== 'all' || searchQuery.trim()) ? `At-risk exposure across ${filteredMetrics.count} doctors` : 'Preventable recovery reserve'}
              </p>
            </div>
          </div>

          {/* Visual Analytics & Protocol Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Card: Chart (Network View) OR Doctor Diagnostic Card (Single Doctor Focus) */}
            <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
              {selectedPhysician ? (
                <div className="space-y-5">
                  <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
                    <div>
                      <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                        Doctor Diagnostic Profile: Dr. {selectedPhysician.physician_name}
                      </h3>
                      <p className="text-xs text-slate-500">Live clinical benchmarking vs specialty standards</p>
                    </div>
                    <span className="text-[10px] font-extrabold font-mono text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-950/60 px-2.5 py-1 rounded-full">
                      Specialty {selectedPhysician.specialty || 'General'}
                    </span>
                  </div>

                  {/* Visual Diagnostic Meters */}
                  <div className="space-y-4">
                    {/* Meter 1: Clean Claims Approval */}
                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-700 dark:text-slate-300">Clean Claims Approval Compliance</span>
                        <span className="font-mono font-black text-emerald-600">
                          {selectedPhysician.physician_performance?.clean_claims_approval_rate_pct || (100 - (selectedPhysician.fraud_rate_pct || 0)).toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 h-2.5 rounded-full overflow-hidden">
                        <div
                          className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, selectedPhysician.physician_performance?.clean_claims_approval_rate_pct || (100 - (selectedPhysician.fraud_rate_pct || 0)))}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[11px] text-slate-400">
                        <span>Peer threshold: 89.5%</span>
                        <span>{selectedPhysician.clean_claims_count?.toLocaleString()} approved encounters</span>
                      </div>
                    </div>

                    {/* Meter 2: Fraud Exposure Rate */}
                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-bold text-slate-700 dark:text-slate-300">Fraud Anomaly Exposure Rate</span>
                        <span className={`font-mono font-black ${(selectedPhysician.fraud_rate_pct || 0) >= 10.0 ? 'text-rose-600' : 'text-amber-600'}`}>
                          {selectedPhysician.fraud_rate_pct}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 h-2.5 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${(selectedPhysician.fraud_rate_pct || 0) >= 10.0 ? 'bg-rose-500' : 'bg-amber-500'}`}
                          style={{ width: `${Math.min(100, (selectedPhysician.fraud_rate_pct || 0) * 4)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[11px] text-slate-400">
                        <span>Facility threshold: 10.48%</span>
                        <span>{selectedPhysician.fraud_claims_count} flagged cases</span>
                      </div>
                    </div>

                    {/* Meter 3: Average Encounter Cost */}
                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-between text-xs">
                      <div>
                        <span className="font-bold text-slate-700 dark:text-slate-300 block">Average Claim Amount</span>
                        <span className="text-[11px] text-slate-400">Specialty peer average: $155.96</span>
                      </div>
                      <div className="text-right">
                        <span className="text-base font-black font-mono text-slate-900 dark:text-white block">
                          {formatCurrency(selectedPhysician.avg_claim_amount || (selectedPhysician.total_billed_amount / selectedPhysician.total_claims))}
                        </span>
                        <span className="text-[10px] font-bold text-emerald-600">Within Standard Variance</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
                        {(riskFilter !== 'all' || searchQuery.trim()) ? `Top Flagged in ${riskFilter === 'all' ? 'Filtered' : riskFilter.toUpperCase()} Cohort (Claims vs Fraud)` : 'Top Flagged Treating Physicians (Claims vs Fraud Cases)'}
                      </h3>
                      <p className="text-xs text-slate-500">
                        {(riskFilter !== 'all' || searchQuery.trim()) ? `Showing top 8 of ${filteredPhysicians.length} filtered physicians in active cohort` : 'Ranked by volume of audit anomalies across the facility'}
                      </p>
                    </div>
                    <span className="text-[10px] font-extrabold font-mono text-slate-500 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                      {(riskFilter !== 'all' || searchQuery.trim()) ? `${Math.min(8, filteredPhysicians.length)} Plotted` : 'Top 8 Volume'}
                    </span>
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topFraudPhysicians}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f040" />
                        <XAxis dataKey="physician_name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="total_claims" name="Total Claims" fill="#3b82f6" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                        <Bar dataKey="fraud_claims_count" name="Fraud Cases" fill="#ef4444" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Right Card: Fraud Reasons & Audit Protocol */}
            <div className="glass-card rounded-2xl p-6 space-y-4 shadow-sm border border-slate-200/80 dark:border-slate-800">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                  {selectedPhysician ? `Anomaly Triggers: Dr. ${selectedPhysician.physician_name}` : 'Multi-Layer Anomaly Detection Protocols'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Automated screening layers calibrated against clinical benchmarks</p>
              </div>

              <div className="space-y-2.5">
                {(selectedPhysician?.fraud_detection?.top_fraud_reasons || [
                  'Layer 1: Exact Duplicate Invoice / Service Combination',
                  'Layer 2: Daily Clinical Frequency Threshold (>15 per day)',
                  'Layer 3: Isolation Forest Multi-Dimensional Outlier Score >= 0.15',
                  'Layer 4: Specialty Benchmark Price Discrepancy'
                ]).map((reason: string, idx: number) => (
                  <div key={idx} className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-between text-xs hover:border-slate-300 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-950 text-rose-600 flex items-center justify-center font-black text-xs shrink-0">
                        L{idx + 1}
                      </div>
                      <span className="font-semibold text-slate-700 dark:text-slate-200">{reason}</span>
                    </div>
                    <span className="text-[10px] font-extrabold px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 shrink-0">
                      Audit Flag
                    </span>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-2xl bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-950/40 dark:to-indigo-950/40 border border-primary-200 dark:border-primary-800 text-xs shadow-sm">
                <span className="font-extrabold text-primary-900 dark:text-primary-200 block mb-1">
                  Compliance Officer Protocol:
                </span>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  {selectedPhysician?.fraud_detection?.audit_recommendation ||
                    'High Priority: Immediate manual review of flagged duplicate claims and abnormal procedure quantities. Cross-examine patient invoices with electronic hospital admission stamps.'}
                </p>
              </div>
            </div>
          </div>

          {/* Sample Invoices Table with Search & Pagination */}
          <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                  {selectedPhysician ? `Audit Queue for Dr. ${selectedPhysician.physician_name}` : (riskFilter !== 'all' || searchQuery.trim()) ? `Audit Queue: ${riskFilter === 'all' ? 'Filtered' : riskFilter.toUpperCase()} Cohort (${filteredClaims.length} Claims)` : 'Institutional Flagged Claims Audit Queue'}
                </h3>
                <p className="text-xs text-slate-500">
                  {selectedPhysician
                    ? `Showing ${filteredClaims.length} flagged clinical encounter records`
                    : (riskFilter !== 'all' || searchQuery.trim())
                    ? `Showing ${filteredClaims.length} claims from ${filteredPhysicians.length} filtered physicians`
                    : `Screened claim lines flagged across healthcare facility (${filteredClaims.length} records)`}
                </p>
              </div>

              <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto justify-end">
                {/* Search within table */}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
                  <input
                    type="text"
                    value={invoiceSearch}
                    onChange={(e) => {
                      setInvoiceSearch(e.target.value);
                      setInvoicesPage(1);
                    }}
                    placeholder="Filter invoice, patient, code..."
                    className="pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                {selectedPhysician && (
                  <div className="flex items-center p-1 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs">
                    <button
                      onClick={() => setClaimsViewMode('flagged')}
                      className={`px-2.5 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                        claimsViewMode === 'flagged' ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      Flagged ({selectedPhysician.fraud_cases_count || 0})
                    </button>
                    <button
                      onClick={() => setClaimsViewMode('all_db')}
                      className={`px-2.5 py-1 rounded-lg font-bold transition-all cursor-pointer flex items-center gap-1 ${
                        claimsViewMode === 'all_db' ? 'bg-primary-600 text-white shadow-sm' : 'text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {loadingDbClaims && <RefreshCw className="w-3 h-3 animate-spin" />}
                      <span>Live DB ({liveDbClaims.length})</span>
                    </button>
                  </div>
                )}

                <button
                  onClick={handleExportClaimsCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-200 transition-colors cursor-pointer"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-400 font-extrabold uppercase tracking-wider text-[10px]">
                    <th className="pb-3 pl-2">Invoice #</th>
                    <th className="pb-3">Treating Physician</th>
                    <th className="pb-3">Patient / Member ID</th>
                    <th className="pb-3">Service Date</th>
                    <th className="pb-3">Gross Amount</th>
                    <th className="pb-3">Fraud Score</th>
                    <th className="pb-3">Primary Trigger</th>
                    <th className="pb-3 pr-2 text-right">Audit Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {paginatedClaims.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400 font-sans">
                        No claim records found matching criteria.
                      </td>
                    </tr>
                  ) : (
                    paginatedClaims.map((item: any, i: number) => {
                      const isExpanded = expandedClaimId === (item.claim_id || item.id || `idx-${i}`);
                      const score = Number(item.fraud_score || 0);
                      const isHighRisk = score >= 0.15 || (item.is_fraud === true);
                      const primaryReason = item.fraud_reasons?.[0] || (isHighRisk ? 'Isolation Forest statistical anomaly' : 'Standard telemetry claim');

                      return (
                        <React.Fragment key={`${item.claim_id || item.id || 'claim'}-${i}`}>
                          <tr
                            onClick={() => setExpandedClaimId(isExpanded ? null : (item.claim_id || item.id || `idx-${i}`))}
                            className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/60 cursor-pointer transition-colors ${
                              isExpanded ? 'bg-primary-50/30 dark:bg-primary-950/20' : ''
                            }`}
                          >
                            <td className="py-3 pl-2 font-mono font-bold text-primary-600 dark:text-primary-400 flex items-center gap-1.5">
                              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                              <span>{item.invoice_no || `INV-${i+1}`}</span>
                            </td>
                            <td className="py-3 font-semibold text-slate-800 dark:text-slate-200">
                              Dr. {item.treating_physician || selectedPhysicianName}
                            </td>
                            <td className="py-3 text-slate-600 dark:text-slate-300">
                              {item.patient_name ? `${item.patient_name}` : item.member_id || 'M_REDACTED'}
                              <span className="text-[10px] text-slate-400 block font-mono">ID: {item.member_id}</span>
                            </td>
                            <td className="py-3 text-slate-500 font-mono">{item.service_date ? String(item.service_date).substring(0, 10) : '2025-05-28'}</td>
                            <td className="py-3 font-mono font-bold text-slate-900 dark:text-white">{formatCurrency(item.gross_claim_amount || 0)}</td>
                            <td className="py-3">
                              <span className={`font-mono font-bold px-2 py-0.5 rounded text-[11px] ${
                                isHighRisk ? 'bg-rose-100 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300' : 'bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300'
                              }`}>
                                {score.toFixed(3)}
                              </span>
                            </td>
                            <td className="py-3 text-[11px] text-slate-600 dark:text-slate-300 max-w-xs truncate" title={primaryReason}>
                              {primaryReason}
                            </td>
                            <td className="py-3 pr-2 text-right">
                              <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold ${
                                isHighRisk
                                  ? 'bg-rose-600 text-white shadow-sm'
                                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                              }`}>
                                {isHighRisk ? 'Immediate Audit' : 'Pass / Normal'}
                              </span>
                            </td>
                          </tr>

                          {/* Expanded Claim Audit Drawer */}
                          {isExpanded && (
                            <tr className="bg-slate-50 dark:bg-slate-900/60">
                              <td colSpan={8} className="p-4 text-xs">
                                <div className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">Clinical Diagnosis</span>
                                    <span className="font-mono font-bold text-slate-900 dark:text-white text-sm mt-0.5 block">
                                      ICD-10: {item.primary_diag_code || 'Unspecified'}
                                    </span>
                                    <span className="text-[11px] text-slate-500 mt-0.5 block">Specialty: {item.specialty || item.specialty_name || 'General Clinic'}</span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">Procedure & Encounter</span>
                                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-200 mt-0.5 block">
                                      CPT: {item.provider_treat_code || item.cpt_code || 'General'} ({item.service_category || 'Clinical Encounter'})
                                    </span>
                                    <span className="text-[11px] text-slate-500 mt-0.5 block">Facility: {item.provider_name || 'Sinan Healthcare'}</span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">Audit Directive</span>
                                    <p className="text-slate-700 dark:text-slate-300 mt-0.5 leading-relaxed font-medium">
                                      {item.recommended_action || (isHighRisk ? 'Suspend reimbursement and verify clinical note against EHR records.' : 'Standard automated approval.')}
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Invoices Table Pagination Bar */}
            {totalInvoicesPages > 1 && (
              <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-100 dark:border-slate-800 text-xs">
                <span className="text-slate-500">
                  Showing {(invoicesPage - 1) * invoicesPerPage + 1} to {Math.min(invoicesPage * invoicesPerPage, filteredClaims.length)} of {filteredClaims.length} claims
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setInvoicesPage(Math.max(1, invoicesPage - 1))}
                    disabled={invoicesPage === 1}
                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2.5 py-1 font-mono font-bold text-slate-700 dark:text-slate-300">
                    {invoicesPage} / {totalInvoicesPages}
                  </span>
                  <button
                    onClick={() => setInvoicesPage(Math.min(totalInvoicesPages, invoicesPage + 1))}
                    disabled={invoicesPage === totalInvoicesPages}
                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          REPORT VIEW 2: IN-PATIENT / OUT-PATIENT ANALYSIS
         ════════════════════════════════════════════════════════════════════════ */}
      {activeReport === 'physician-inpatient-outpatient' && (
        <div className="space-y-6">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-amber-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Out-Patient Claims (O)</span>
                <span className="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/60 text-amber-600">
                  <Activity className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white font-mono">
                {(selectedPhysician ? selectedPhysician.inpatient_outpatient_analysis?.outpatient_claims_count || selectedPhysician.total_claims : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.totalClaims : pillarsSummary.inpatient_outpatient_analysis?.active_outpatient_claims || 29797).toLocaleString()}
              </p>
              <p className="text-xs text-amber-600 font-semibold mt-1">
                {(riskFilter !== 'all' || searchQuery.trim()) ? `Across ${filteredMetrics.count} active physicians` : 'Ambulatory clinic consultations'}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-indigo-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {selectedPhysician ? 'Physician Inpatient (I)' : 'Enterprise Inpatient (I)'}
                </span>
                <span className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600">
                  <Stethoscope className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-indigo-600 font-mono">
                {selectedPhysician ? (selectedPhysician.inpatient_outpatient_analysis?.inpatient_claims_count || 0) : pillarsSummary.inpatient_outpatient_analysis?.enterprise_inpatient_admissions_count || 347}
              </p>
              <p className="text-xs text-slate-500 mt-1">Hospitalized admissions benchmark</p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-emerald-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Active Service Split</span>
                <span className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-emerald-600 font-mono">
                100.0% Outpatient
              </p>
              <p className="text-xs text-slate-500 mt-1">0 Inpatient surgical admissions</p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-blue-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {(riskFilter !== 'all' || searchQuery.trim()) ? 'Cohort Billed Total' : 'Outpatient Billed Total'}
                </span>
                <span className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600">
                  <DollarSign className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-blue-600 font-mono">
                {formatCurrency(selectedPhysician ? selectedPhysician.inpatient_outpatient_analysis?.outpatient_billed_amount || selectedPhysician.total_billed_amount : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.billedAmount : pillarsSummary.inpatient_outpatient_analysis?.active_outpatient_amount || 4647048.22)}
              </p>
              <p className="text-xs text-slate-500 mt-1">Consultation & pharmacy total</p>
            </div>
          </div>

          {/* Breakdown & Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Facility Profile Card */}
            <div className="glass-card rounded-2xl p-6 space-y-4 shadow-sm border border-slate-200/80 dark:border-slate-800">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Clinical Admission Profile</h3>
                <p className="text-xs text-slate-500 mt-0.5">Ambulatory clinic care vs hospitalized inpatient admission</p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-300 font-bold">Facility Primary Workflow:</span>
                  <span className="font-extrabold text-slate-900 dark:text-white font-mono">100.0% Outpatient (O)</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 h-3 rounded-full overflow-hidden flex">
                  <div className="bg-amber-500 h-full w-full" />
                </div>
                <div className="flex justify-between text-[11px] text-slate-400">
                  <span>Outpatient (29,797 records in Sinan Clinic)</span>
                  <span>Inpatient (347 records in Regional Benchmark)</span>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/30 border border-indigo-200/80 dark:border-indigo-800/80 text-xs space-y-2">
                <span className="font-extrabold text-indigo-900 dark:text-indigo-300 flex items-center gap-1.5">
                  <Info className="w-4 h-4 text-indigo-600 shrink-0" />
                  Enterprise Benchmark Observation:
                </span>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
                  {selectedPhysician?.inpatient_outpatient_analysis?.inpatient_cross_facility_note ||
                    `${pillarsSummary.inpatient_outpatient_analysis?.active_facility || 'Sinan'} facility claims are ambulatory outpatient consultations. Across enterprise reference datasets, Inpatient admissions total 347 cases treated by 27 designated specialists.`}
                </p>
              </div>
            </div>

            {/* Service Category Breakdown Chart */}
            <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-1">
                Outpatient Service Category Breakdown
              </h3>
              <p className="text-xs text-slate-500 mb-4">Distribution across clinical lines, pharmacy, and diagnostic lab</p>
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serviceCategoryChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f040" />
                    <XAxis dataKey="category" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="claims" name="Encounter Claims" fill="#3b82f6" radius={[6, 6, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Inpatient Specialists Leaderboard */}
          <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
            <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-1">
              Top Enterprise Inpatient Specialists (Hospitalized Admissions Benchmark)
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Physicians leading inpatient admissions and complex surgical procedures across regional hospitals
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {(pillarsSummary.inpatient_outpatient_analysis?.top_inpatient_physicians || [
                'Tindra Olsen (36 cases)',
                'Mitch Carpentier (28 cases)',
                'Torkel Izzo (26 cases)',
                'Rafal Barrera (26 cases)',
                'Rian Ljung (23 cases)',
              ]).map((docStr: string, idx: number) => {
                const parts = docStr.split('(');
                const name = parts[0]?.trim();
                const count = parts[1]?.replace(')', '').trim() || `${30 - idx * 2} cases`;
                return (
                  <div
                    key={idx}
                    className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 flex flex-col justify-between"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 rounded-lg bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 font-extrabold flex items-center justify-center text-[10px]">
                        #{idx + 1}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400">Inpatient Care</span>
                    </div>
                    <p className="font-bold text-slate-900 dark:text-white text-xs">Dr. {name}</p>
                    <div className="mt-3 pt-2 border-t border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between">
                      <span className="text-[10px] text-slate-500 font-medium">Admissions:</span>
                      <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400 text-xs">
                        {count}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          REPORT VIEW 3: CHANGE OF AMOUNT
         ════════════════════════════════════════════════════════════════════════ */}
      {activeReport === 'physician-change-amount' && (
        <div className="space-y-6">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-blue-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Gross Billed Amount</span>
                <span className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600">
                  <DollarSign className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white font-mono">
                {formatCurrency(selectedPhysician ? selectedPhysician.total_billed_amount : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.billedAmount : pillarsSummary.change_of_amount?.active_facility_gross_amount || summary.total_billed_amount || 4647048.22)}
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                {(riskFilter !== 'all' || searchQuery.trim()) ? `Total across ${filteredMetrics.count} active physicians` : 'Original submitted claims value'}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-emerald-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Net Payable Amount</span>
                <span className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-emerald-600 font-mono">
                {formatCurrency(selectedPhysician ? selectedPhysician.change_of_amount?.net_payable_amount || selectedPhysician.total_billed_amount : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.billedAmount : pillarsSummary.change_of_amount?.active_facility_net_payable || 4647048.22)}
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium">Reconciled settlement pool</p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-teal-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Facility Variance Rate</span>
                <span className="p-2 rounded-xl bg-teal-50 dark:bg-teal-950/60 text-teal-600">
                  <Activity className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-teal-600 font-mono">
                {Number(selectedPhysician?.change_of_amount?.variance_rate_pct || 0).toFixed(2)}%
              </p>
              <p className="text-xs text-emerald-600 font-semibold mt-1">100% Direct Match</p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-indigo-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Enterprise Adjustments</span>
                <span className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600">
                  <SlidersHorizontal className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-indigo-600 font-mono">
                {formatCurrency(pillarsSummary.change_of_amount?.enterprise_total_variance || 1174823.12)}
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium">Across 27,780 modifications</p>
            </div>
          </div>

          {/* Variance Breakdown & Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Reconciliation Status Card */}
            <div className="glass-card rounded-2xl p-6 space-y-4 shadow-sm border border-slate-200/80 dark:border-slate-800">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Active Facility Reconciliation Status</h3>
              
              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 flex items-start gap-3.5">
                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-xs">
                  <p className="font-extrabold text-emerald-900 dark:text-emerald-200 text-sm">
                    {selectedPhysician?.change_of_amount?.status || '100% Billing Match (Net = Gross, Zero Deductions)'}
                  </p>
                  <p className="text-emerald-700 dark:text-emerald-300 mt-1 leading-relaxed font-medium">
                    {selectedPhysician?.change_of_amount?.change_summary ||
                      'All submitted claim lines from this facility reflect direct matching between gross and net payable amounts without unexpected co-pay deviations or post-payment billing cuts.'}
                  </p>
                </div>
              </div>

              {/* Deductions Breakdown Table */}
              <div className="space-y-2 text-xs">
                <span className="font-extrabold text-slate-700 dark:text-slate-300 block">Enterprise Deductions Portfolio:</span>
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
                  <span className="text-slate-600 dark:text-slate-300 font-medium">Contractual Provider Discounts:</span>
                  <span className="font-extrabold font-mono text-slate-900 dark:text-white">
                    {formatCurrency(pillarsSummary.change_of_amount?.enterprise_discount_amount || 866023.27)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
                  <span className="text-slate-600 dark:text-slate-300 font-medium">Patient Deductible & Co-pay:</span>
                  <span className="font-extrabold font-mono text-slate-900 dark:text-white">
                    {formatCurrency(pillarsSummary.change_of_amount?.enterprise_deductible_amount || 263655.88)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
                  <span className="text-slate-600 dark:text-slate-300 font-medium">Coinsurance Deductions:</span>
                  <span className="font-extrabold font-mono text-slate-900 dark:text-white">
                    {formatCurrency(pillarsSummary.change_of_amount?.enterprise_coinsurance_amount || 45258.09)}
                  </span>
                </div>
              </div>
            </div>

            {/* Top Variance Physicians in Enterprise Dataset */}
            <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-1">
                Top Financial Variance Physicians (Enterprise Benchmark)
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                Physicians with the highest frequency of post-submission deductions in general hospitals
              </p>

              <div className="space-y-3">
                {(pillarsSummary.change_of_amount?.top_variance_physicians || [
                  'Thale Bjerke (658 claims adjusted)',
                  'Gianpiero Mauro (592 claims adjusted)',
                  'Jordi Cousin (545 claims adjusted)',
                ]).map((item: string, idx: number) => {
                  const parts = item.split('(');
                  const name = parts[0]?.trim();
                  const count = parts[1]?.replace(')', '').trim() || `${600 - idx * 40} adjusted`;
                  return (
                    <div
                      key={idx}
                      className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 font-black flex items-center justify-center text-xs">
                          #{idx + 1}
                        </div>
                        <div>
                          <p className="font-extrabold text-slate-900 dark:text-slate-100">Dr. {name}</p>
                          <p className="text-[10px] text-slate-400">Post-submission billing reconciliation</p>
                        </div>
                      </div>
                      <span className="font-bold font-mono text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-2.5 py-1 rounded-lg">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Financial Settlement Claims Audit Table */}
          <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                  {selectedPhysician ? `Financial Reconciliation for Dr. ${selectedPhysician.physician_name}` : 'Institutional Financial Settlement Audit Queue'}
                </h3>
                <p className="text-xs text-slate-500">
                  Detailed reconciliation of submitted gross amounts vs payer net payable disbursements
                </p>
              </div>

              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-3 py-1.5 rounded-xl border border-emerald-200 dark:border-emerald-800 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                <span>Zero Variance Verified</span>
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-400 font-extrabold uppercase tracking-wider text-[10px]">
                    <th className="pb-3 pl-2">Invoice #</th>
                    <th className="pb-3">Treating Physician</th>
                    <th className="pb-3">Patient Name</th>
                    <th className="pb-3">Gross Submitted</th>
                    <th className="pb-3">Net Payable</th>
                    <th className="pb-3">Variance / Deductions</th>
                    <th className="pb-3 pr-2 text-right">Settlement Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {paginatedClaims.map((item: any, i: number) => {
                    const gross = Number(item.gross_claim_amount || 0);
                    return (
                      <tr key={`${item.invoice_no || 'inv'}-${i}`} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/60 transition-colors">
                        <td className="py-3 pl-2 font-mono font-bold text-primary-600 dark:text-primary-400">
                          {item.invoice_no || `INV-${i + 1}`}
                        </td>
                        <td className="py-3 font-semibold text-slate-800 dark:text-slate-200">
                          Dr. {item.treating_physician || selectedPhysicianName}
                        </td>
                        <td className="py-3 text-slate-600 dark:text-slate-300">
                          {item.patient_name || item.member_id || 'M_REDACTED'}
                        </td>
                        <td className="py-3 font-mono font-bold text-slate-900 dark:text-white">
                          {formatCurrency(gross)}
                        </td>
                        <td className="py-3 font-mono font-bold text-emerald-600">
                          {formatCurrency(gross)}
                        </td>
                        <td className="py-3 font-mono text-slate-400">
                          $0.00 (0.0%)
                        </td>
                        <td className="py-3 pr-2 text-right">
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 inline-flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            <span>100% Match</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          REPORT VIEW 4: PHYSICIAN PERFORMANCE
         ════════════════════════════════════════════════════════════════════════ */}
      {activeReport === 'physician-performance' && (
        <div className="space-y-6">
          {/* Top KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-blue-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {selectedPhysician ? 'Physician Encounters' : 'Monitored Physicians'}
                </span>
                <span className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/60 text-blue-600">
                  <Stethoscope className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white font-mono">
                {selectedPhysician ? selectedPhysician.total_claims : (riskFilter !== 'all' || searchQuery.trim()) ? filteredMetrics.count : (pillarsSummary.physician_performance?.total_active_physicians || 74)}
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                {selectedPhysician ? `Total claims for Dr. ${selectedPhysician.physician_name}` : (riskFilter !== 'all' || searchQuery.trim()) ? `Active in ${riskFilter === 'all' ? 'search' : riskFilter} cohort` : 'Active registered doctors'}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-emerald-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Clean Claims Approval</span>
                <span className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-emerald-600 font-mono">
                {selectedPhysician
                  ? (selectedPhysician.physician_performance?.clean_claims_approval_rate_pct || (100 - (selectedPhysician.fraud_rate_pct || 0)).toFixed(2))
                  : (riskFilter !== 'all' || searchQuery.trim())
                  ? filteredMetrics.cleanRate.toFixed(2)
                  : (pillarsSummary.physician_performance?.overall_clean_claim_rate_pct || 89.52)}%
              </p>
              <p className="text-xs text-emerald-600 font-semibold mt-1">Normal audit compliance</p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-teal-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {selectedPhysician ? 'Performance Rating' : 'Satisfactory Doctors'}
                </span>
                <span className="p-2 rounded-xl bg-teal-50 dark:bg-teal-950/60 text-teal-600">
                  <Award className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-teal-600 font-mono">
                {selectedPhysician ? (selectedPhysician.physician_performance?.performance_rating?.split(' ')[0] || 'Clean') : (riskFilter !== 'all' || searchQuery.trim()) ? (riskFilter === 'clean' ? filteredMetrics.count : 0) : (pillarsSummary.physician_performance?.satisfactory_clean_physicians_count || 19)}
              </p>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                {selectedPhysician ? (selectedPhysician.physician_performance?.benchmark_comparison || 'Standard compliance') : 'Low risk (<5% fraud rate)'}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-5 border-t-4 border-t-rose-600 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                  {selectedPhysician ? 'Audit Status' : 'Audit Flagged Doctors'}
                </span>
                <span className="p-2 rounded-xl bg-rose-50 dark:bg-rose-950/60 text-rose-600">
                  <ShieldAlert className="w-4 h-4" />
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-rose-600 font-mono">
                {selectedPhysician ? (selectedPhysician.physician_performance?.audit_status?.split(' ')[0] || 'Routine') : (riskFilter !== 'all' || searchQuery.trim()) ? (riskFilter === 'clean' ? 0 : filteredMetrics.count) : (riskCounts.high + riskCounts.review)}
              </p>
              <p className="text-xs text-rose-500 font-semibold mt-1">
                {selectedPhysician ? (selectedPhysician.physician_performance?.audit_status || 'Routine review') : 'Under clinical review'}
              </p>
            </div>
          </div>

          {/* Leaderboard & Benchmarks */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Clean Compliance Doctors */}
            <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-1">
                Benchmark Quality Leaders (Top Clean Approval Rates)
              </h3>
              <p className="text-xs text-slate-500 mb-4">Physicians exhibiting exemplary claims integrity</p>

              <div className="space-y-3">
                {(pillarsSummary.physician_performance?.top_clean_physicians || [
                  'Amelia Alvarado (1.65% fraud, 98.35% clean)',
                  'Adrian Boucher (2.31% fraud, 97.69% clean)',
                ]).map((cleanStr: string, idx: number) => {
                  const parts = cleanStr.split('(');
                  const name = parts[0]?.trim();
                  const stats = parts[1]?.replace(')', '').trim();
                  return (
                    <div
                      key={idx}
                      onClick={() => setSelectedPhysicianName(name)}
                      className="p-4 rounded-2xl bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 flex items-center justify-between text-xs cursor-pointer hover:bg-emerald-100/60 transition-colors"
                    >
                      <div className="flex items-center gap-3.5">
                        <Award className="w-5 h-5 text-emerald-600 shrink-0" />
                        <div>
                          <p className="font-extrabold text-slate-900 dark:text-white">Dr. {name}</p>
                          <p className="text-[10px] text-emerald-700 dark:text-emerald-300 font-medium">{stats}</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 rounded-full text-[10px] font-extrabold bg-emerald-200/80 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200">
                        Top Performer
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Claims Volume Doctors */}
            <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white mb-1">
                Top Institutional Volume Physicians
              </h3>
              <p className="text-xs text-slate-500 mb-4">Highest volume of processed clinical encounters</p>

              <div className="space-y-3">
                {(pillarsSummary.physician_performance?.top_claims_volume_physicians || [
                  'Ita Trujillo (2,382 claims)',
                  'Dominic Cotter (1,888 claims)',
                  'Marie-Louise Seifert (1,834 claims)',
                ]).map((volStr: string, idx: number) => {
                  const parts = volStr.split('(');
                  const name = parts[0]?.trim();
                  const vol = parts[1]?.replace(')', '').trim();
                  return (
                    <div
                      key={idx}
                      onClick={() => setSelectedPhysicianName(name)}
                      className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 flex items-center justify-between text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="w-7 h-7 rounded-xl bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-black flex items-center justify-center text-xs">
                          #{idx + 1}
                        </div>
                        <div>
                          <p className="font-extrabold text-slate-800 dark:text-slate-200">Dr. {name}</p>
                          <p className="text-[10px] text-slate-400">Clinical Encounter Leader</p>
                        </div>
                      </div>
                      <span className="font-extrabold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-3 py-1 rounded-xl">
                        {vol}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Full Physician Leaderboard Table with Pagination */}
          <div className="glass-card rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-800">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                  Physician Performance Audit Queue
                </h3>
                <p className="text-xs text-slate-500">Complete registry of 74 physicians with peer ratings and audit recommendation</p>
              </div>
              <span className="text-xs text-slate-400 font-mono font-medium">
                Showing {filteredPhysicians.length} of {physiciansList.length} physicians
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-400 font-extrabold uppercase tracking-wider text-[10px]">
                    <th className="pb-3 pl-2 cursor-pointer hover:text-slate-900 dark:hover:text-white" onClick={() => handleSort('name')}>
                      <div className="flex items-center gap-1">
                        <span>Physician</span>
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="pb-3">Specialty</th>
                    <th className="pb-3 cursor-pointer hover:text-slate-900 dark:hover:text-white" onClick={() => handleSort('claims')}>
                      <div className="flex items-center gap-1">
                        <span>Total Claims</span>
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="pb-3 cursor-pointer hover:text-slate-900 dark:hover:text-white" onClick={() => handleSort('clean')}>
                      <div className="flex items-center gap-1">
                        <span>Clean Rate</span>
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="pb-3 cursor-pointer hover:text-slate-900 dark:hover:text-white" onClick={() => handleSort('fraud')}>
                      <div className="flex items-center gap-1">
                        <span>Fraud Rate</span>
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="pb-3 cursor-pointer hover:text-slate-900 dark:hover:text-white" onClick={() => handleSort('billed')}>
                      <div className="flex items-center gap-1">
                        <span>Total Billed</span>
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="pb-3">Performance Rating</th>
                    <th className="pb-3 pr-2 text-right">Audit Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {paginatedDirectory.map((p: any, idx: number) => {
                    const rate = p.fraud_rate_pct ?? (p.fraud_detection?.fraud_rate_pct || 0);
                    const clean = (100 - rate).toFixed(2);
                    const isSelected = selectedPhysicianName === p.physician_name;

                    return (
                      <tr
                        key={`${p.physician_name || 'doc'}-${idx}`}
                        onClick={() => setSelectedPhysicianName(p.physician_name)}
                        className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/80 cursor-pointer transition-colors ${
                          isSelected ? 'bg-primary-50/70 dark:bg-primary-950/40 border-l-4 border-l-primary-600' : ''
                        }`}
                      >
                        <td className="py-3 pl-2 font-bold text-slate-900 dark:text-white flex items-center gap-2">
                          <UserCheck className={`w-3.5 h-3.5 ${isSelected ? 'text-primary-600' : 'text-slate-400'}`} />
                          <span>Dr. {p.physician_name}</span>
                        </td>
                        <td className="py-3 text-slate-500 font-medium">Specialty {p.specialty || 'General'}</td>
                        <td className="py-3 font-mono font-bold">{p.total_claims}</td>
                        <td className="py-3 font-mono font-bold text-emerald-600">{clean}%</td>
                        <td className={`py-3 font-mono font-bold ${rate >= 10.0 ? 'text-rose-600' : rate >= 5.0 ? 'text-amber-600' : 'text-slate-500'}`}>
                          {rate}%
                        </td>
                        <td className="py-3 font-mono font-bold">{formatCurrency(p.total_billed_amount)}</td>
                        <td className="py-3">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold flex items-center gap-1 w-fit ${
                            rate < 5.0
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                              : rate < 10.0
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                              : 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              rate < 5.0 ? 'bg-emerald-500' : rate < 10.0 ? 'bg-amber-500' : 'bg-rose-500'
                            }`} />
                            {rate < 5.0 ? 'Satisfactory' : rate < 10.0 ? 'Review Required' : 'High Risk'}
                          </span>
                        </td>
                        <td className="py-3 pr-2 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPhysicianName(p.physician_name);
                              setActiveReport('physician-fraud');
                            }}
                            className="text-primary-600 dark:text-primary-400 hover:underline text-[11px] font-bold inline-flex items-center gap-1 cursor-pointer"
                          >
                            <span>Inspect</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Directory Pagination Bar */}
            {totalDirectoryPages > 1 && (
              <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-100 dark:border-slate-800 text-xs">
                <span className="text-slate-500">
                  Showing {(directoryPage - 1) * directoryPerPage + 1} to {Math.min(directoryPage * directoryPerPage, filteredPhysicians.length)} of {filteredPhysicians.length} physicians
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setDirectoryPage(Math.max(1, directoryPage - 1))}
                    disabled={directoryPage === 1}
                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-2.5 py-1 font-mono font-bold text-slate-700 dark:text-slate-300">
                    {directoryPage} / {totalDirectoryPages}
                  </span>
                  <button
                    onClick={() => setDirectoryPage(Math.min(totalDirectoryPages, directoryPage + 1))}
                    disabled={directoryPage === totalDirectoryPages}
                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default PhysicianReportsView;
